import { LOADING_STATUSES } from "@constants";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import chatsService from "@services/chatsService";
import { dashboardService } from "@services/dashboardService";
import { setPeriod, setPeriods } from "./filtersSlice";

export const fetchAllDashboard = createAsyncThunk(
  "dashboard/fetchAll",
  async (_, { dispatch, getState }) => {
    const periodsResult = await dispatch(fetchPeriods());

    const periodsPayload = periodsResult?.payload;
    const periodItems = Array.isArray(periodsPayload?.data)
      ? periodsPayload.data
      : [];
    const isGeral = (p) =>
      (!p.city || p.city === "") &&
      (!p.nps_segment || p.nps_segment === "") &&
      (!p.client_type || p.client_type === "");

    const lastMonthly = periodItems
      .filter((p) => p.type === "monthly" && isGeral(p))
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))[0];

    const selectedPeriod =
      lastMonthly?.name ||
      periodItems[0]?.name ||
      periodItems[0]?.key ||
      getState().filters.period;

    dispatch(setPeriod(selectedPeriod));
    dispatch(
      setPeriods(
        periodItems.map((p) => ({
          id: p.id,
          name: p.name || p.key,
          start_date: p.start_date || null,
          type: p.type || null,
          city: p.city ?? "",
          nps_segment: p.nps_segment ?? "",
          client_type: p.client_type ?? "",
        })),
      ),
    );

    await Promise.all([
      dispatch(fetchBenchmarking()),
      dispatch(fetchChats()),
      dispatch(fetchDashboardOverview()),
      dispatch(fetchOverviewHistory()),
      dispatch(fetchDrivers()),
      dispatch(fetchDriversAll()),
      dispatch(fetchDriverEssence()),
      dispatch(fetchDriverMetrics()),
      dispatch(fetchDriverMetricsHistory()),
      dispatch(fetchMetricsHistory()),
      dispatch(fetchDriverMetricsMonthly()),
      dispatch(fetchDriverMonthly()),
      dispatch(fetchInsights()),
      dispatch(fetchJourney()),
      dispatch(fetchRespondentsMonthly()),
      dispatch(fetchSegmentation()),
      dispatch(fetchSegmentationHistory()),
      dispatch(fetchCooperacao()),
    ]);
  },
);

export const fetchActionPlan = createAsyncThunk(
  "dashboard/fetchActionPlan",
  async (_, { getState }) => {
    return await dashboardService.getActionPlan(getState().filters);
  },
);

export const fetchBenchmarking = createAsyncThunk(
  "dashboard/fetchBenchmarking",
  async (_, { getState }) => {
    return await dashboardService.getBenchmarking(getState().filters);
  },
);

export const fetchCooperacao = createAsyncThunk(
  "dashboard/fetchCooperacao",
  async () => {
    return await dashboardService.getCooperacao();
  },
);

export const fetchChats = createAsyncThunk(
  "dashboard/fetchChats",
  async (_, { getState }) => {
    return await dashboardService.getChats(getState().filters);
  },
);

export const fetchDashboardOverview = createAsyncThunk(
  "dashboard/fetchOverview",
  async (_, { getState }) => {
    return await dashboardService.getOverview(getState().filters);
  },
);

export const fetchOverviewHistory = createAsyncThunk(
  "dashboard/fetchOverviewHistory",
  async (_, { getState }) => {
    return await dashboardService.getOverviewHistory(getState().filters);
  },
);

export const fetchDrivers = createAsyncThunk(
  "dashboard/fetchDrivers",
  async (_, { getState }) => {
    return await dashboardService.getDrivers(getState().filters);
  },
);

export const fetchDriversAll = createAsyncThunk(
  "dashboard/fetchDriversAll",
  async () => {
    return await dashboardService.getDriversAll();
  },
);

export const fetchDriverMetrics = createAsyncThunk(
  "dashboard/fetchDriverMetrics",
  async (_, { getState }) => {
    return await dashboardService.getDriversMetrics(getState().filters);
  },
);

export const fetchDriverMetricsMonthly = createAsyncThunk(
  "dashboard/fetchDriverMetricsMonthly",
  async (_, { getState }) => {
    return await dashboardService.getDriversMetricsMonthly(getState().filters);
  },
);

export const fetchDriverMonthly = createAsyncThunk(
  "dashboard/fetchDriverMonthly",
  async (_, { getState }) => {
    return await dashboardService.getDriversMonthly(getState().filters);
  },
);

export const fetchDriverEssence = createAsyncThunk(
  "dashboard/fetchDriverEssence",
  async (_, { getState }) => {
    return await dashboardService.getDriversEssence(getState().filters);
  },
);

export const fetchInsights = createAsyncThunk(
  "dashboard/fetchInsights",
  async (_, { getState }) => {
    return await dashboardService.getInsights(getState().filters);
  },
);

export const fetchJourney = createAsyncThunk(
  "dashboard/fetchJourney",
  async (_, { getState }) => {
    return await dashboardService.getJourney(getState().filters);
  },
);

export const fetchPeriods = createAsyncThunk(
  "dashboard/fetchPeriods",
  async (_, { getState }) => {
    return await dashboardService.getPeriods();
  },
);

export const fetchRespondentsMonthly = createAsyncThunk(
  "dashboard/fetchRespondentsMonthly",
  async (_, { getState }) => {
    return await dashboardService.getRespondentsMonthly(getState().filters);
  },
);

export const fetchSegmentation = createAsyncThunk(
  "dashboard/fetchSegmentation",
  async (_, { getState }) => {
    return await dashboardService.getSegmentation(getState().filters);
  },
);

export const fetchSegmentationHistory = createAsyncThunk(
  "dashboard/fetchSegmentationHistory",
  async (_, { getState }) => {
    return await dashboardService.getSegmentationHistory(getState().filters);
  },
);

export const fetchDriverMetricsHistory = createAsyncThunk(
  "dashboard/fetchDriverMetricsHistory",
  async () => {
    return await dashboardService.getDriversMetricsHistory();
  },
);

export const fetchMetricsHistory = createAsyncThunk(
  "dashboard/fetchMetricsHistory",
  async (_, { getState }) => {
    return await dashboardService.getMetricsHistory(getState().filters);
  },
);

// Seções que dependem dos filtros da tela (período, cidade, segmento NPS e
// vínculo). Inclui os históricos de Tendências e Segmentação: sem eles na
// lista, trocar a cidade atualizava os cards do período mas os gráficos de
// evolução continuavam mostrando o geral.
export const fetchFilteredDashboard = createAsyncThunk(
  "dashboard/fetchFiltered",
  async (_, { dispatch }) => {
    await Promise.all([
      dispatch(fetchDashboardOverview()),
      dispatch(fetchOverviewHistory()),
      dispatch(fetchDrivers()),
      dispatch(fetchDriversAll()),
      dispatch(fetchDriverEssence()),
      dispatch(fetchDriverMetrics()),
      dispatch(fetchDriverMetricsMonthly()),
      dispatch(fetchDriverMonthly()),
      dispatch(fetchMetricsHistory()),
      dispatch(fetchBenchmarking()),
      dispatch(fetchInsights()),
      dispatch(fetchJourney()),
      dispatch(fetchSegmentation()),
      dispatch(fetchSegmentationHistory()),
      dispatch(fetchRespondentsMonthly()),
    ]);
  },
);

// Carrega o dashboard de um período mantendo os demais filtros. Usado pelo
// relatório PDF: o período escolhido no modal precisa estar no store antes
// de montar as páginas — antes só o rótulo mudava e os números continuavam
// os do período da tela.
export const loadDashboardForPeriod = createAsyncThunk(
  "dashboard/loadForPeriod",
  async (periodName, { dispatch, getState }) => {
    if (periodName && getState().filters.period !== periodName) {
      dispatch(setPeriod(periodName));
    }
    await dispatch(fetchFilteredDashboard());
  },
);

export const updateChatTitle = createAsyncThunk(
  "dashboard/updateChatTitle",
  async ({ chatId, title }, { rejectWithValue }) => {
    try {
      const response = await chatsService.put(chatId, { title });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao atualizar título");
    }
  },
);

export const deleteChatStatus = createAsyncThunk(
  "dashboard/deleteChatStatus",
  async (chatId, { rejectWithValue }) => {
    try {
      const response = await chatsService.put(chatId, { status: "deleted" });
      return { chatId, response };
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao deletar chat");
    }
  },
);

export const updateOverviewMetric = createAsyncThunk(
  "dashboard/updateOverviewMetric",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const response = await dashboardService.updateOverview(id, payload);
      dispatch(fetchDashboardOverview());
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao atualizar meta");
    }
  },
);

export const updateJourneyMetric = createAsyncThunk(
  "dashboard/updateJourneyMetric",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const response = await dashboardService.updateJourney(id, payload);
      dispatch(fetchJourney());
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao atualizar meta");
    }
  },
);

const addAsyncCases = (builder, thunk, key) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key].status = LOADING_STATUSES.LOADING;
      state[key].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[key].status = LOADING_STATUSES.SUCCEEDED;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      state[key].status = LOADING_STATUSES.FAILED;
      state[key].error = action.error.message || "Falha ao carregar dados";
    });
};

const emptySection = () => ({
  data: null,
  status: LOADING_STATUSES.IDLE,
  error: null,
});

const initialState = {
  actionPlan: emptySection(),
  benchmarking: emptySection(),
  cooperacao: emptySection(),
  chats: emptySection(),
  drivers: emptySection(),
  driversAll: emptySection(),
  driverEssence: emptySection(),
  driverMetrics: emptySection(),
  driverMetricsHistory: emptySection(),
  metricsHistory: emptySection(),
  driverMetricsMonthly: emptySection(),
  driverMonthly: emptySection(),
  insights: emptySection(),
  journey: emptySection(),
  overview: emptySection(),
  overviewHistory: emptySection(),
  periods: emptySection(),
  respondentsMonthly: emptySection(),
  segmentation: emptySection(),
  segmentationHistory: emptySection(),
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboard: () => initialState,
    setChatMessages: (state, action) => {
      const { chatId, messages } = action.payload;
      if (state.chats.data && state.chats.data.data) {
        const chatIndex = state.chats.data.data.findIndex(
          (chat) => chat.id === chatId,
        );
        if (chatIndex >= 0) {
          state.chats.data.data[chatIndex] = {
            ...state.chats.data.data[chatIndex],
            messages,
            mensagens: messages,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, fetchActionPlan, "actionPlan");
    addAsyncCases(builder, fetchBenchmarking, "benchmarking");
    addAsyncCases(builder, fetchCooperacao, "cooperacao");
    addAsyncCases(builder, fetchChats, "chats");
    addAsyncCases(builder, fetchDriverEssence, "driverEssence");
    addAsyncCases(builder, fetchDriverMetrics, "driverMetrics");
    addAsyncCases(builder, fetchDriverMetricsHistory, "driverMetricsHistory");
    addAsyncCases(builder, fetchMetricsHistory, "metricsHistory");
    addAsyncCases(builder, fetchDriverMetricsMonthly, "driverMetricsMonthly");
    addAsyncCases(builder, fetchDriverMonthly, "driverMonthly");
    addAsyncCases(builder, fetchDrivers, "drivers");
    addAsyncCases(builder, fetchDriversAll, "driversAll");
    addAsyncCases(builder, fetchInsights, "insights");
    addAsyncCases(builder, fetchJourney, "journey");
    addAsyncCases(builder, fetchDashboardOverview, "overview");
    addAsyncCases(builder, fetchOverviewHistory, "overviewHistory");
    addAsyncCases(builder, fetchPeriods, "periods");
    addAsyncCases(builder, fetchRespondentsMonthly, "respondentsMonthly");
    addAsyncCases(builder, fetchSegmentation, "segmentation");
    addAsyncCases(builder, fetchSegmentationHistory, "segmentationHistory");

    // Handle updateChatTitle
    builder
      .addCase(updateChatTitle.pending, (state) => {
        state.chats.status = LOADING_STATUSES.LOADING;
      })
      .addCase(updateChatTitle.fulfilled, (state, action) => {
        state.chats.status = LOADING_STATUSES.SUCCEEDED;
        if (state.chats.data && state.chats.data.data) {
          const updatedChat = action.payload.data;
          const chatIndex = state.chats.data.data.findIndex(
            (chat) => chat.id === updatedChat.id,
          );
          if (chatIndex >= 0) {
            state.chats.data.data[chatIndex] = updatedChat;
          }
        }
      })
      .addCase(updateChatTitle.rejected, (state, action) => {
        state.chats.status = LOADING_STATUSES.FAILED;
        state.chats.error = action.payload || "Erro ao atualizar chat";
      })
      .addCase(deleteChatStatus.fulfilled, (state, action) => {
        state.chats.status = LOADING_STATUSES.SUCCEEDED;
        if (state.chats.data && state.chats.data.data) {
          state.chats.data.data = state.chats.data.data.filter(
            (chat) => chat.id !== action.payload.chatId,
          );
        }
      })
      .addCase(deleteChatStatus.rejected, (state, action) => {
        state.chats.status = LOADING_STATUSES.FAILED;
        state.chats.error = action.payload || "Erro ao deletar chat";
      });
  },
});

export const { clearDashboard, setChatMessages } = dashboardSlice.actions;
export const selectOverview = (state) => state.dashboard.overview;
export const selectRespondentsMonthly = (state) =>
  state.dashboard.respondentsMonthly;
export const selectJourney = (state) => state.dashboard.journey;
export const selectDrivers = (state) => state.dashboard.drivers;
export const selectDriverMetrics = (state) => state.dashboard.driverMetrics;
export const selectDriverMetricsMonthly = (state) =>
  state.dashboard.driverMetricsMonthly;
export const selectDriverMonthly = (state) => state.dashboard.driverMonthly;
export const selectTrends = (state) => state.dashboard.trends;
export const selectSegmentation = (state) => state.dashboard.segmentation;
export const selectBenchmarking = (state) => state.dashboard.benchmarking;
export const selectActionPlan = (state) => state.dashboard.actionPlan;
export const selectCooperacao = (state) => state.dashboard.cooperacao;
export default dashboardSlice.reducer;
